import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { NormalInput } from '../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { NormalTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import HeadlessTextArea from '../../shared/component/headless/textarea/HeadlessTextArea';
import { AlertPosition, AlertType, SupportArea, SupportType, TextAlignment, getSupportAreaLabel } from '../../shared/enum/EnumRepository';
import { sanitizeString } from '../../shared/util/sanitize';
import { useSupport } from '../../shared/hooks/useApi/useSupport';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useLanguageStore } from '../../shared/store/languageStore';
import { popIn } from '../../shared/component/Animation';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { paperTextureModal } from '../../shared/component/paperTexture';
import { SUPPORT_MESSAGE_MAX_LENGTH } from '../../shared/constants/limits';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ExhibitionVenueRequestModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { user } = useAuth();
  const { language } = useLanguageStore();
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { createSupport } = useSupport();
  const { enqueueAlert, setTextLengthExceededAlert } = useStandardAlertStore();
  const initialTitle = standardModal?.value ?? "";
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (title !== initialTitle || description.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [title, description, initialTitle, setIsUserInput]);

  if (standardModal == null) return null;

  if (user == null) return null;

  const handleChange = async () => {
    const sanitizedTitle = sanitizeString(title);
    const sanitizedDescription = sanitizeString(description);
    if (!sanitizedTitle || !sanitizedDescription) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SUPPORT_CONTENT_REQUIRED'),
      });
      return;
    }
    const areaLabel = getSupportAreaLabel(SupportArea.EXHIBITION, language);
    await createSupport(user.aui, {
      type: SupportType.VENUE_REQUEST,
      title: sanitizedTitle,
      message: `[${areaLabel}] ${sanitizedDescription}`,
    });
  };

  const handleChangeWithLimit = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > SUPPORT_MESSAGE_MAX_LENGTH) {
      setTextLengthExceededAlert(SUPPORT_MESSAGE_MAX_LENGTH);
      return;
    }
    setDescription(e.target.value);
  }

  return (
    <SupportModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <SupportModalComp>
        <HeaderRow>
          <Title>{getModalMessage('EXHIBITION_VENUE_REQUEST_TITLE')}</Title>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={() => clearModal()}
          />
        </HeaderRow>
        <HeadlessInput
          type={'text'}
          value={title}
          handleChange={(e) => setTitle(e.target.value)}
          placeholder={getModalMessage('EXHIBITION_VENUE_REQUEST_TITLE_PLACEHOLDER')}
          StyledInput={NormalInput}
        />
        <DescriptionWrapper>
          <LimitDiv>
            {description.toString().length} / {SUPPORT_MESSAGE_MAX_LENGTH}
          </LimitDiv>
          <HeadlessTextArea
            alignment={TextAlignment.LEFT}
            content={description}
            placeholder={getModalMessage('EXHIBITION_VENUE_REQUEST_PLACEHOLDER')}
            handleChange={handleChangeWithLimit}
            StyledTextArea={NormalTextArea}
            $minRows={7}
            $maxRows={7}
          />
        </DescriptionWrapper>
        <HeadlessBtn
          value={getBtnName('SUBMIT')}
          handleClick={handleChange}
          StyledBtn={SubmitButton}
        />
      </SupportModalComp>
    </SupportModalFrame>
  );
};

export const SupportModalFrame = styled.div<{ $isClosing: boolean }>`
  position: relative;
  padding: 20px;
  width: 640px;
  height: fit-content;
  
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'scale(0.8)' : 'scale(1)')};
  
  transition: all 0.3s ease-in;
  ${paperTextureModal}
  ${popIn}
`;

const SupportModalComp = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 2vh 1vw;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin-top: 2.5vh;
`;

const DescriptionWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 15vh;
`;

const LimitDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  z-index: 2;
`;

export default ExhibitionVenueRequestModal;
